import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Trash, Lightning, ClockClockwise, CaretRight, CircleNotch } from "@phosphor-icons/react";
import {
    listWatches,
    deleteWatch,
    simulateWatch,
    listWatchReports, errText,
} from "@/lib/api";
import { CreateWatchDialog } from "@/components/CreateWatchDialog";
import { DailyReportPanel } from "@/components/DailyReportPanel";

export const MonitoringPanel = () => {
    const [watches, setWatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeWatchId, setActiveWatchId] = useState(null);
    const [reports, setReports] = useState([]);
    const [activeReport, setActiveReport] = useState(null);
    const [simulating, setSimulating] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const list = await listWatches();
            setWatches(list);
            if (list.length > 0 && !activeWatchId) {
                setActiveWatchId(list[0].id);
            }
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    // When the active watch changes, refetch its reports.
    useEffect(() => {
        if (!activeWatchId) {
            setReports([]);
            setActiveReport(null);
            return;
        }
        (async () => {
            try {
                const r = await listWatchReports(activeWatchId);
                setReports(r);
                setActiveReport(r[0] || null);
            } catch {
                setReports([]);
                setActiveReport(null);
            }
        })();
    }, [activeWatchId]);

    const handleDelete = async (id) => {
        try {
            await deleteWatch(id);
            toast.success("Watch deleted");
            if (activeWatchId === id) {
                setActiveWatchId(null);
                setReports([]);
                setActiveReport(null);
            }
            refresh();
        } catch (e) {
            toast.error(errText(e, "Failed to delete"));
        }
    };

    const handleSimulate = async (id) => {
        setSimulating(true);
        const tId = toast.loading("Simulating full day (5 slot runs + report)…");
        try {
            const report = await simulateWatch(id);
            toast.success("Report generated", { id: tId });
            setActiveWatchId(id);
            const r = await listWatchReports(id);
            setReports(r);
            setActiveReport(report);
        } catch (e) {
            toast.error(errText(e, "Simulation failed"), { id: tId });
        } finally {
            setSimulating(false);
        }
    };

    const activeWatch = watches.find((w) => w.id === activeWatchId);

    return (
        <div data-testid="monitoring-panel" className="flex-1 grid grid-cols-1 lg:grid-cols-[320px_1fr] min-h-0">
            {/* Watches sidebar */}
            <aside className="border-r-2 border-ink bg-white flex flex-col min-h-0">
                <div className="p-4 border-b-2 border-ink flex items-center justify-between gap-2">
                    <div>
                        <h2 className="font-display text-lg font-black tracking-tighter">Ad Watches</h2>
                        <p className="mono-label text-neutral-500">Daily automated SERP monitors</p>
                    </div>
                    <CreateWatchDialog onCreated={refresh} />
                </div>
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="p-6 text-center text-neutral-500">
                            <CircleNotch size={18} weight="bold" className="animate-spin inline-block" />
                        </div>
                    ) : watches.length === 0 ? (
                        <div className="p-6 text-center">
                            <p className="mono-label text-neutral-500 mb-3">No watches yet</p>
                            <CreateWatchDialog onCreated={refresh} />
                        </div>
                    ) : (
                        <ul>
                            {watches.map((w) => {
                                const active = w.id === activeWatchId;
                                return (
                                    <li key={w.id}>
                                        <button
                                            data-testid={`watch-item-${w.id}`}
                                            onClick={() => setActiveWatchId(w.id)}
                                            className={`w-full text-left px-4 py-3 border-b border-neutral-200 flex items-start justify-between gap-2 hover:bg-neutral-50 transition ${active ? "bg-neutral-100 border-l-4 border-l-klein" : ""}`}
                                        >
                                            <div className="min-w-0 flex-1">
                                                <div className="font-heading text-sm font-bold text-ink truncate">
                                                    {w.business_type}
                                                </div>
                                                <div className="mono-label text-neutral-500 truncate">
                                                    {w.city} · {w.country_label} · {w.timezone}
                                                </div>
                                                {w.label && (
                                                    <div className="text-xs italic text-neutral-500 truncate mt-0.5">
                                                        {w.label}
                                                    </div>
                                                )}
                                                {w.last_report_date && (
                                                    <div className="mono-label text-emerald-700 mt-0.5">
                                                        Last report · {w.last_report_date}
                                                    </div>
                                                )}
                                            </div>
                                            <CaretRight
                                                size={14}
                                                weight="bold"
                                                className={`shrink-0 mt-0.5 transition ${active ? "text-klein" : "text-neutral-400"}`}
                                            />
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </aside>

            {/* Reports view */}
            <div className="flex flex-col min-h-0 overflow-y-auto">
                {activeWatch ? (
                    <>
                        <div className="p-4 border-b-2 border-ink flex flex-wrap items-center justify-between gap-3">
                            <div className="min-w-0">
                                <h2 className="font-display text-xl font-black tracking-tighter truncate">
                                    {activeWatch.business_type}
                                </h2>
                                <p className="mono-label mt-0.5">
                                    {activeWatch.city} · {activeWatch.country_label} · scheduler tz {activeWatch.timezone}
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    data-testid="simulate-watch-btn"
                                    disabled={simulating}
                                    onClick={() => handleSimulate(activeWatch.id)}
                                    className="brutal-btn bg-klein border border-klein text-white font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5 disabled:opacity-50"
                                    title="Fire all 5 slot runs back-to-back and generate today's report immediately"
                                >
                                    {simulating ? (
                                        <CircleNotch size={13} weight="bold" className="animate-spin" />
                                    ) : (
                                        <Lightning size={13} weight="bold" />
                                    )}
                                    {simulating ? "Simulating" : "Simulate Full Day"}
                                </button>
                                <button
                                    data-testid="delete-watch-btn"
                                    onClick={() => {
                                        if (window.confirm(`Delete watch for "${activeWatch.business_type}" in ${activeWatch.city}?`)) {
                                            handleDelete(activeWatch.id);
                                        }
                                    }}
                                    className="brutal-btn bg-white border border-red-600 text-red-600 font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                                >
                                    <Trash size={13} weight="bold" />
                                    Delete
                                </button>
                            </div>
                        </div>

                        {/* Report history chips */}
                        {reports.length > 0 && (
                            <div className="px-4 py-3 border-b border-neutral-200 flex items-center gap-2 overflow-x-auto shrink-0">
                                <ClockClockwise size={13} weight="bold" className="text-neutral-500 shrink-0" />
                                <span className="mono-label text-neutral-500 shrink-0 mr-1">History</span>
                                {reports.map((r) => (
                                    <button
                                        key={r.id}
                                        data-testid={`report-chip-${r.report_date}`}
                                        onClick={() => setActiveReport(r)}
                                        className={`shrink-0 border font-mono uppercase text-[10px] tracking-widest font-bold px-2.5 py-1 transition ${
                                            activeReport?.id === r.id
                                                ? "bg-ink text-white border-ink"
                                                : "bg-white text-ink border-neutral-300 hover:border-ink"
                                        }`}
                                    >
                                        {r.report_date}
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="p-5 flex-1">
                            {activeReport ? (
                                <DailyReportPanel report={activeReport} />
                            ) : (
                                <div className="text-center py-16 max-w-md mx-auto">
                                    <Eye size={36} weight="bold" className="mx-auto text-neutral-400 mb-3" />
                                    <h3 className="font-heading text-lg font-bold text-ink">
                                        No reports yet for this watch
                                    </h3>
                                    <p className="text-sm text-neutral-600 mt-2">
                                        Reports auto-generate after the 9 PM slot in {activeWatch.timezone}. To see one immediately, click <b>Simulate Full Day</b> above — this fires all 5 slot searches now and generates the report.
                                    </p>
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex items-center justify-center p-8">
                        <div className="text-center max-w-md">
                            <Eye size={44} weight="bold" className="mx-auto text-neutral-400 mb-4" />
                            <h2 className="font-display text-2xl font-black tracking-tighter text-ink">
                                Daily Ad Monitor
                            </h2>
                            <p className="mono-label mt-2 mb-4 text-neutral-600">
                                Auto-scans the Google Ads section 5× a day, then hands you a Daily Opportunity Report — who bid when, when the crowd is thinnest, and where the client should day-part.
                            </p>
                            <CreateWatchDialog onCreated={refresh} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
